import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import NavPrimaryDropdown from 'components/navV2/NavPrimaryDropdown';
import NavLink from 'components/navV2/NavLink';
import NavPrimarySubMenuContainer from 'components/navV2/navPrimarySubMenu/NavPrimarySubMenuContainer';

import hasChildren from 'util/hasChildren';
import rmvSpaceAndSpecial from 'util/rmvSpaceAndSpecial';

import css from './NavPrimaryMapper.module.less';

// NOTE: this component should become the NavPrimary.
const NavPrimaryMapper = ({ navData, className, activeLinkId, userContext }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState();
  const activeNavItemIndex = navData.reduce((acc, node, i) => {
    if (activeLinkId === node.id) return i;
    return acc;
  }, null);

  const menuTimerIdRef = useRef(null);

  // Do not show the overlay for links
  const activeItemIsDropdown = hasChildren(navData[openMenuIndex]) > 0;
  const showOverlay = isMenuOpen && activeItemIsDropdown;

  // set the initial openMenuIndex to the active index;
  useEffect(() => {
    if (navData.length && !openMenuIndex && openMenuIndex !== 0) {
      setOpenMenuIndex(activeNavItemIndex);
    }
  }, [openMenuIndex, activeLinkId, navData, activeNavItemIndex]);

  useEffect(
    () => () => {
      clearTimeout(menuTimerIdRef.current);
    },
    [menuTimerIdRef]
  );

  const handleSetIsMenuOpen = (bool, index) => {
    setOpenMenuIndex(index);
    setIsMenuOpen(bool);
  };

  const handleSetMenuTimerId = (setTimeoutId) => {
    menuTimerIdRef.current = setTimeoutId;
  };

  const clearMenuTimer = () => {
    clearTimeout(menuTimerIdRef.current);
  };

  const renderDropdown = (node = {}, index) => {
    const classes = cx('navPrimary__dropdown', [
      `navPrimary__dropdown--${rmvSpaceAndSpecial(node.title)}`,
    ]);
    return (
      <NavPrimaryDropdown
        className={classes}
        icon={node.icon}
        label={node.title}
        link={{ href: node.path }}
        showCaret
        isActive={activeLinkId === node.id}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={handleSetIsMenuOpen}
        setMenuTimerId={handleSetMenuTimerId}
        openMenuIndex={openMenuIndex}
        index={index}
        activeNavItemIndex={activeNavItemIndex}
        clearMenuTimer={clearMenuTimer}
      >
        <NavPrimarySubMenuContainer activeNavType={node.id} userContext={userContext} />
      </NavPrimaryDropdown>
    );
  };

  const renderLink = (node = {}, index) => {
    return (
      <NavLink
        href={node.path}
        variant="PrimaryNavLink"
        newTag={node.newTag}
        icon={node.icon}
        isActive={activeLinkId === node.id}
        className={`navPrimary__${node.id}`}
        openMenuIndex={openMenuIndex}
        index={index}
        activeNavItemIndex={activeNavItemIndex}
        setMenuTimerId={handleSetMenuTimerId}
        setMenuOpenIndex={handleSetIsMenuOpen}
        clearMenuTimer={clearMenuTimer}
      >
        {node.title}
      </NavLink>
    );
  };

  if (!navData.length) return null;

  return (
    <div id="primary-nav" className={cx('navPrimary__container', className)}>
      <ul className="navPrimary__links">
        {navData.map((node, i) => {
          const nodeHasChildren = hasChildren(node);
          const classes = cx('navPrimary__link', {
            'navPrimary__link--hasChildren': nodeHasChildren,
          });

          return (
            <li key={i} className={classes}>
              {nodeHasChildren ? renderDropdown(node, i) : renderLink(node, i)}
            </li>
          );
        })}
      </ul>
      <div
        className={cx(css.overlay, {
          [css.active]: showOverlay,
        })}
      />
    </div>
  );
};

NavPrimaryMapper.propTypes = {
  navData: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  activeLinkId: PropTypes.string,
  userContext: PropTypes.shape({}),
};

NavPrimaryMapper.defaultProps = {
  navData: [],
  activeLinkId: '',
};

export default NavPrimaryMapper;
